/**
 * Template Builder API client.
 *
 * 11 wrappers around the backend REST endpoints defined in
 * docs/spec/platform-template-management.md.
 *
 * The backend endpoints live under /api/v1/templates and are practice-scoped
 * via the authenticated session (no practice_id parameter on the client side).
 *
 * Status: scaffold (2026-05-26). Endpoints exist as specs; backend
 * implementation lands in PR #12 + part 2 stacked PR. This client is
 * production-shaped but currently calls TODO endpoints.
 */

import api from "../core/index-core";

const BASE = "/api/v1/templates";

/** Fetch the read-only section library catalog. Loader is a backend singleton. */
export async function getSectionLibrary() {
  const res = await api.get(`${BASE}/library`);
  return res.data;
}

/** List templates for the authenticated practice. Pass includeArchived to include soft-deleted templates. */
export async function listTemplates({ specialtyTag, includeArchived = false, page = 1, pageSize = 50 } = {}) {
  const params = { page, page_size: pageSize, include_archived: includeArchived };
  if (specialtyTag) params.specialty_tag = specialtyTag;
  const res = await api.get(BASE, { params });
  return res.data;
}

/** Get one template plus its current version definition. */
export async function getTemplate(id) {
  const res = await api.get(`${BASE}/${id}`);
  const { definition, ...template } = res.data;
  return { template, definition };
}

/** Get a specific version of a template. */
export async function getVersion(id, versionNumber) {
  const res = await api.get(`${BASE}/${id}/versions/${versionNumber}`);
  return res.data;
}

/** Create a new template with an initial version. */
export async function createTemplate({ name, specialty_tag, definition }) {
  const res = await api.post(BASE, { name, specialty_tag, definition });
  const { definition: def, ...template } = res.data;
  return { template, definition: def };
}

/** Save a new version of an existing template. */
export async function updateTemplate(id, { definition }) {
  const res = await api.put(`${BASE}/${id}`, { definition });
  const { definition: def, ...template } = res.data;
  return { template, definition: def };
}

/** Clone an existing template's current version into a new template. */
export async function cloneTemplate(id, { newName }) {
  const res = await api.post(`${BASE}/${id}/clone`, { new_name: newName });
  const { definition, ...template } = res.data;
  return { template, definition };
}

/** Soft-delete a template (sets archived_at). */
export async function archiveTemplate(id) {
  const res = await api.post(`${BASE}/${id}/archive`);
  return res.data;
}

/** Reverse archiveTemplate. */
export async function restoreTemplate(id) {
  const res = await api.post(`${BASE}/${id}/restore`);
  return res.data;
}

/**
 * Generate a draft template definition from a plain-language description.
 * No DB write: the backend returns a definition the builder loads for the
 * admin to refine and then Save.
 */
export async function scaffoldTemplate({ description, specialty_tag, intended_visit_type, name }) {
  const res = await api.post(`${BASE}/scaffold`, {
    description,
    specialty_tag: specialty_tag || "behavioral_health",
    intended_visit_type: intended_visit_type || "follow_up",
    name: name || "Generated template",
  });
  // The endpoint wraps the result: { definition, selected_section_ids, rationale }.
  // The builder loads the definition itself.
  return res.data?.definition ?? res.data;
}

/** List all versions of a template. */
export async function listVersions(id, { page = 1, pageSize = 50 } = {}) {
  const res = await api.get(`${BASE}/${id}/versions`, {
    params: { page, page_size: pageSize },
  });
  return res.data;
}

/** Make a specific version the current version of its template. */
export async function promoteVersion(id, versionNumber) {
  const res = await api.post(`${BASE}/${id}/versions/${versionNumber}/promote`);
  return res.data;
}

/** List audit-log events for a template (newest-first as returned by the backend). */
export async function getAuditEvents(id, { page = 1, pageSize = 100 } = {}) {
  const res = await api.get(`${BASE}/${id}/audit`, { params: { page, page_size: pageSize } });
  return res.data;
}

/** Restore a specific version as the template's current content. */
export async function restoreVersion(id, versionNumber) {
  const res = await api.post(`${BASE}/${id}/versions/${versionNumber}/restore`);
  const { definition, ...template } = res.data;
  return { template, definition };
}

/** Render a sample note against this template's current version. */
export async function previewTemplate(id, { transcript }) {
  const res = await api.post(`${BASE}/${id}/preview`, { transcript });
  return res.data;
}

/**
 * Render a sample note against an unsaved draft definition.
 *
 * Used by the builder live-preview pane before the template has been saved
 * for the first time. The backend endpoint accepts the unsaved definition
 * in the body so the admin can iterate without committing to a save.
 */
export async function previewDraft({ definition, transcript }) {
  const res = await api.post(`${BASE}/preview-draft`, { definition, transcript });
  return res.data;
}
